import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Redirect, Route, Switch } from 'react-router-dom/cjs/react-router-dom';
import { Tabs, Typography } from 'antd';
import PageMeta from 'components/PageMeta/PageMeta';
import { InternalUsers } from './__components/InternalUsers';
import { ProviderUsers } from './__components/ProvidersUsers';
import { FamilyUsers } from './__components/FamilyUsers';
import { useSelector } from 'react-redux';
import { selectRoles } from 'features/auth';
import { ROLES } from 'constants';

export const USER_STATUSES = {
  FORCE_CHANGE_PASSWORD: 'Force Change Password',
  RESET_REQUIRED: 'Reset Required',
  UNCONFIRMED: 'Unconfirmed',
};

const USER_TYPES = {
  INTERNAL: 'internal',
  PROVIDERS: 'providers',
  FAMILIES: 'families',
};

export const FAMILY_ROLES_WITH_ACCESS_TO_USERS_PAGE = [
  ROLES.FAMILY_MANAGER,
  ROLES.FAMILY_COORDINATOR,
  ROLES.PARENT_SERVICES_SPECIALIST,
];
export const PROVIDER_ROLES_WITH_ACCESS_TO_USERS_PAGE = [
  ROLES.PROVIDER_MANAGER,
  ROLES.PROVIDER_COORDINATOR,
  ROLES.PROVIDER_SUPPORT_SPECIALIST,
];
const INTERNAL_ROLES_WITH_ACCESS_TO_USERS_PAGE = [ROLES.SYSTEM_ADMIN];

export const ROLES_WITH_ACCESS_TO_USERS_PAGE = [
  ...FAMILY_ROLES_WITH_ACCESS_TO_USERS_PAGE,
  ...PROVIDER_ROLES_WITH_ACCESS_TO_USERS_PAGE,
  ...INTERNAL_ROLES_WITH_ACCESS_TO_USERS_PAGE,
];

export default function Users() {
  const history = useHistory();
  const location = history.location;
  const _activeTab = location?.pathname?.includes?.('/internal')
    ? USER_TYPES.INTERNAL
    : location?.pathname?.includes?.('/providers')
    ? USER_TYPES.PROVIDERS
    : USER_TYPES.FAMILIES;

  const roles = useSelector(selectRoles);

  const hasAccessToProviderUsersTab = useMemo(() => {
    return (
      roles.includes(ROLES.SYSTEM_ADMIN) ||
      roles.find((role) => PROVIDER_ROLES_WITH_ACCESS_TO_USERS_PAGE.includes(role))
    );
  }, [roles]);
  const hasAccessToFamilyUsersTab = useMemo(() => {
    return (
      roles.includes(ROLES.SYSTEM_ADMIN) || roles.find((role) => FAMILY_ROLES_WITH_ACCESS_TO_USERS_PAGE.includes(role))
    );
  }, [roles]);
  const hasAccessToInternalUsersTab = useMemo(() => {
    return roles.includes(ROLES.SYSTEM_ADMIN);
  }, [roles]);

  const tabItems = useMemo(() => {
    return [
      ...(hasAccessToInternalUsersTab
        ? [
            {
              label: 'Internal',
              key: USER_TYPES.INTERNAL,
            },
          ]
        : []),
      ...(hasAccessToProviderUsersTab
        ? [
            {
              label: 'Providers',
              key: USER_TYPES.PROVIDERS,
            },
          ]
        : []),
      ...(hasAccessToFamilyUsersTab
        ? [
            {
              label: 'Families',
              key: USER_TYPES.FAMILIES,
            },
          ]
        : []),
    ];
  }, [hasAccessToProviderUsersTab, hasAccessToFamilyUsersTab, hasAccessToInternalUsersTab]);

  return (
    <>
      <Typography.Title level={1} className="!mb-0">
        Users
      </Typography.Title>

      <Tabs
        items={tabItems}
        activeKey={_activeTab}
        onChange={(key) => {
          history.push(`/admin/users/${key}`);
        }}
        className="mb-4"
      />

      <Switch>
        {hasAccessToInternalUsersTab ? (
          <Route path="/admin/users/internal">
            <PageMeta title="Internal users" />

            <InternalUsers />
          </Route>
        ) : (
          <Route path="/admin/users/internal">
            <Redirect to="/admin/users/" />
          </Route>
        )}

        {hasAccessToProviderUsersTab ? (
          <Route path="/admin/users/providers">
            <ProviderUsers />
          </Route>
        ) : (
          <Route path="/admin/users/providers">
            <Redirect to="/admin/users/" />
          </Route>
        )}

        {hasAccessToFamilyUsersTab ? (
          <Route path="/admin/users/families">
            <FamilyUsers />
          </Route>
        ) : (
          <Route path="/admin/users/families">
            <Redirect to="/admin/users/" />
          </Route>
        )}

        <Route path="/admin/*">
          <Redirect to={`/admin/users/${tabItems?.[0]?.key}`} />
        </Route>
      </Switch>
    </>
  );
}
